import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { api, resolveAsset } from "../lib/api";

/**
 * Universal tiles grid — fetches tiles for `pageKey` from /api/page-tiles
 * and renders them as a responsive 1 / 2 / 3-up grid matching the style of
 * the Explore and Merch tiles. Designed to be dropped onto any public page.
 *
 * Props:
 *  - pageKey  string   (required, e.g. "home", "retro", "page:about", "explore:podcasts")
 *  - kicker   string   optional section kicker (default: "More")
 *  - heading  string   optional heading (default: "Explore more")
 *  - className string  optional wrapper classes
 *  - hideWhenEmpty bool (default true) — if no tiles, render nothing
 */
export default function PageTilesGrid({
  pageKey,
  kicker = "More",
  heading = "Explore more",
  className = "",
  hideWhenEmpty = true,
}) {
  const [tiles, setTiles] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!pageKey) return undefined;
    setTiles(null);
    api
      .get("/page-tiles", { params: { page_key: pageKey } })
      .then((r) => alive && setTiles(Array.isArray(r.data) ? r.data : []))
      .catch(() => alive && setTiles([]));
    return () => {
      alive = false;
    };
  }, [pageKey]);

  if (tiles === null) {
    return (
      <div
        className={`flex items-center gap-2 text-[var(--brand-body)] ${className}`}
        data-testid={`page-tiles-loading-${pageKey}`}
      >
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (tiles.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div
        className={`rounded-md border border-dashed border-black/15 p-8 text-center text-sm text-[var(--brand-body)] ${className}`}
        data-testid={`page-tiles-empty-${pageKey}`}
      >
        No tiles yet.
      </div>
    );
  }

  return (
    <section
      className={className}
      data-testid={`page-tiles-section-${pageKey}`}
    >
      {heading ? (
        <div className="border-b border-black/10 pb-4">
          {kicker ? (
            <p className="label-kicker text-brand-accent">{kicker}</p>
          ) : null}
          <h2 className="mt-2 font-display text-2xl font-black tracking-tight sm:text-3xl">
            {heading}
          </h2>
        </div>
      ) : null}
      <div
        className={`${heading ? "mt-6" : ""} grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3`}
        data-testid={`page-tiles-grid-${pageKey}`}
      >
        {tiles.map((t, i) => (
          <PageTile key={t.id} tile={t} index={i} />
        ))}
      </div>
    </section>
  );
}

const PageTile = ({ tile, index }) => {
  const bg = tile.tile_color || "#002d75";
  const isExternal =
    tile.link_type === "external" && (tile.external_url || "").trim() !== "";
  const isCustomPage =
    tile.link_type === "custom_page" && (tile.custom_page_slug || "").trim() !== "";
  const isInternal =
    tile.link_type === "internal" && (tile.internal_path || "").trim() !== "";

  const trackClick = () => {
    try {
      api.post(`/page-tiles/${tile.id}/click`).catch(() => {});
    } catch (_) {
      /* non-fatal */
    }
  };

  const bgImage = tile.image_url ? resolveAsset(tile.image_url) : "";

  const content = (
    <>
      {bgImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 transition-opacity duration-500 group-hover:opacity-75"
          style={{ backgroundImage: `url(${bgImage})` }}
          aria-hidden
        />
      ) : null}
      <div
        className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-75"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)",
        }}
        aria-hidden
      />
      <div className="relative flex h-full flex-col justify-between p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/30">
            {isExternal ? (
              <ExternalLink size={20} strokeWidth={2.2} />
            ) : (
              <Sparkles size={20} strokeWidth={2.2} />
            )}
          </div>
          <ArrowUpRight
            size={22}
            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </div>
        <div className="mt-auto">
          <span className="label-kicker text-[#6CABDD]">
            {isExternal ? "External" : "More"}
          </span>
          <h3 className="mt-3 font-display text-2xl font-black leading-[1.1] tracking-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
            {tile.title || "Coming soon"}
          </h3>
          {tile.subtitle ? (
            <p className="mt-3 text-sm leading-relaxed text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
              {tile.subtitle}
            </p>
          ) : null}
          <div className="mt-6 flex items-center border-t border-white/10 pt-4">
            <span className="label-kicker text-white/80">
              {tile.cta_label
                ? tile.cta_label
                : isExternal
                ? "Open link"
                : "Open"}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const sharedProps = {
    "data-testid": `page-tile-${tile.id}`,
    onClick: trackClick,
    className:
      "card-hover group relative flex h-full flex-col justify-between overflow-hidden rounded-md border border-black/10 fade-in-up text-white",
    style: {
      animationDelay: `${index * 70}ms`,
      minHeight: "300px",
      backgroundColor: bg,
    },
  };

  if (isExternal) {
    return (
      <a
        href={tile.external_url}
        target="_blank"
        rel="noopener noreferrer"
        {...sharedProps}
      >
        {content}
      </a>
    );
  }
  if (isCustomPage) {
    return (
      <Link to={`/page/${tile.custom_page_slug}`} {...sharedProps}>
        {content}
      </Link>
    );
  }
  if (isInternal) {
    const path = tile.internal_path.startsWith("/")
      ? tile.internal_path
      : `/${tile.internal_path}`;
    return (
      <Link to={path} {...sharedProps}>
        {content}
      </Link>
    );
  }
  // No destination configured — render as disabled card
  return (
    <div {...sharedProps} style={{ ...sharedProps.style, opacity: 0.85 }}>
      {content}
    </div>
  );
};
